import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import * as Icons from "lucide-react";
import { ArrowLeft, ArrowRight, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PublicShell, Loading, ResourceCard } from "@/components/shared";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function toPascal(s) { return (s || "target").split("-").map((w) => w[0]?.toUpperCase() + w.slice(1)).join(""); }

export function ProblemSolver() {
  const { data, isLoading } = useQuery({ queryKey: ["problems"], queryFn: async () => (await api.get("/problems")).data });
  return (
    <PublicShell>
      <section className="bg-navy grain"><div className="relative mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald">CEO Problem Solver</p>
        <h1 className="mt-3 font-heading text-4xl font-bold tracking-tight text-white sm:text-5xl">What's holding your business back?</h1>
        <p className="mt-3 max-w-2xl text-slate-400">Pick a problem, answer a few questions, and get a tailored action plan of playbooks, packs and tools.</p>
      </div></section>
      <section className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
        {isLoading ? <Loading /> : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.map((p) => {
              const Icon = Icons[toPascal(p.icon)] || Compass;
              return (
                <Link key={p.id} to={`/problem-solver/${p.slug}`} data-testid={`problem-${p.slug}`}
                  className="group flex items-start gap-4 rounded-xl border border-slate-200 bg-white p-5 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgb(0,0,0,0.06)]">
                  <div className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-emerald/10 text-emerald"><Icon className="h-5 w-5" strokeWidth={1.75} /></div>
                  <div><h3 className="font-heading font-semibold text-navy group-hover:text-emerald">{p.title}</h3><p className="mt-1 text-sm text-slate-600">{p.description}</p></div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </PublicShell>
  );
}

export function ProblemDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["problem", slug], queryFn: async () => (await api.get(`/problems/${slug}`)).data });
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  if (isLoading) return <PublicShell><Loading /></PublicShell>;
  if (!data) return <PublicShell><div className="py-24 text-center text-slate-500">Problem not found.</div></PublicShell>;

  const qs = data.questions || [];
  const answer = (opt) => {
    setAnswers({ ...answers, [step]: opt });
    if (step + 1 < qs.length) setStep(step + 1); else submit({ ...answers, [step]: opt });
  };
  const submit = async (finalAnswers) => {
    if (!user) return navigate("/login");
    setBusy(true);
    try { const { data: res } = await api.post(`/problems/${slug}/solve`, { answers: finalAnswers }); setResult(res); }
    catch { navigate("/login"); }
    finally { setBusy(false); }
  };

  return (
    <PublicShell>
      <section className="mx-auto max-w-3xl px-5 py-12 lg:px-8">
        <button onClick={() => navigate("/problem-solver")} className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-navy" data-testid="problem-back"><ArrowLeft className="h-4 w-4" /> All problems</button>
        <h1 className="font-heading text-3xl font-bold text-navy">{data.title}</h1>
        <p className="mt-2 text-slate-600">{data.description}</p>

        {!result ? (
          <div className="mt-8 rounded-xl border border-slate-200 bg-white p-8" data-testid="problem-quiz">
            <p className="text-xs font-bold uppercase tracking-wider text-emerald">Question {step + 1} of {qs.length}</p>
            <h2 className="mt-2 font-heading text-xl font-semibold text-navy">{qs[step]?.q}</h2>
            <div className="mt-6 grid gap-3">
              {qs[step]?.options.map((opt) => (
                <button key={opt} onClick={() => answer(opt)} disabled={busy} data-testid={`problem-option-${opt}`}
                  className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3 text-left font-medium text-navy transition-colors hover:border-emerald hover:bg-emerald/5">
                  {opt} <ArrowRight className="h-4 w-4 text-slate-400" />
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-8" data-testid="problem-result">
            <div className="rounded-xl border border-emerald/30 bg-emerald/5 p-6">
              <p className="text-xs font-bold uppercase tracking-wider text-emerald">Your action plan</p>
              <p className="mt-2 font-heading text-lg font-semibold text-navy">{result.recommendations.next_action}</p>
            </div>
            {result.recommendations.playbooks?.length > 0 && (
              <div className="mt-8"><h3 className="font-heading text-lg font-semibold text-navy">Recommended playbooks</h3>
                <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{result.recommendations.playbooks.map((r) => <ResourceCard key={r.id} r={{ ...r, locked: false }} />)}</div></div>
            )}
            {result.recommendations.packs?.length > 0 && (
              <div className="mt-8"><h3 className="font-heading text-lg font-semibold text-navy">Growth Packs</h3>
                <div className="mt-4 flex flex-wrap gap-3">{result.recommendations.packs.map((p) => <Link key={p.id} to={`/packs/${p.slug}`} className="rounded-lg border border-slate-200 bg-white px-4 py-3 font-semibold text-navy hover:border-emerald">{p.title} →</Link>)}</div></div>
            )}
            <div className="mt-8 flex gap-3">
              <Button onClick={() => { setResult(null); setStep(0); setAnswers({}); }} variant="outline" className="rounded-md" data-testid="problem-restart">Start over</Button>
              <Button onClick={() => navigate("/dashboard")} className="rounded-md bg-navy text-white hover:bg-navysurface" data-testid="problem-to-dashboard">Go to dashboard</Button>
            </div>
          </div>
        )}
      </section>
    </PublicShell>
  );
}
